import { apiV1Paths } from "@Front/Api/helpers/apiClientV1Paths";
import type { IBaseAdmin } from "../../BaseAdminFactory";
import type {
	IGetClientPortalSessionResponse,
	IGetCustomerBySubscriptionIdParams,
	IGetInvoiceByUidResponse,
	IGetPaymentIntentByUidResponse,
	IGetStripeSubscriptionByUidResponse,
	IPostStripeParams,
	IPostStripeResponse,
} from "../Stripe";
import { buildStripeCreatePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";
import { buildStripeInvoicePath, buildStripePaymentIntentPath } from "../stripeFrontContracts";

export function createPostMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (body: IPostStripeParams): Promise<IPostStripeResponse> => {
		const url = new URL(baseUrl);
		try {
			const payload = buildStripeCreatePayload(body);
			return await baseAdmin.postRequest<IPostStripeResponse>(url, payload);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createGetStripeSubscriptionByUidMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (stripe_subscription_id: string): Promise<IGetStripeSubscriptionByUidResponse> => {
		const url = new URL(baseUrl.concat(`/${stripe_subscription_id}`));
		try {
			return await baseAdmin.getRequest<IGetStripeSubscriptionByUidResponse>(url);
		} catch (err) {
			const httpStatus = (err as { http_status?: number })?.http_status;
			if (httpStatus === 404) {
				// Subscription not found (deleted in Stripe, test data, etc.). Do not log or toast.
				return Promise.reject(err);
			}
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createGetClientPortalSessionMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (stripe_subscription_id: string): Promise<IGetClientPortalSessionResponse> => {
		const url = new URL(baseUrl.concat(`/${stripe_subscription_id}/client-portal`));
		try {
			return await baseAdmin.getRequest<IGetClientPortalSessionResponse>(url);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createGetCustomerBySubscriptionIdMethod(baseAdmin: IBaseAdmin, baseUrl: string) {
	return async (subscriptionId: string): Promise<IGetCustomerBySubscriptionIdParams> => {
		const url = new URL(baseUrl.concat(`/${subscriptionId}/customer`));
		try {
			return await baseAdmin.getRequest<IGetCustomerBySubscriptionIdParams>(url);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createGetPaymentIntentByUidMethod(baseAdmin: IBaseAdmin, _baseUrl: string) {
	return async (paymentIntentId: string): Promise<IGetPaymentIntentByUidResponse> => {
		const url = new URL(buildStripePaymentIntentPath(paymentIntentId));
		try {
			return await baseAdmin.getRequest<IGetPaymentIntentByUidResponse>(url);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createPostReconcileOfficeMethod(baseAdmin: IBaseAdmin) {
	return async (): Promise<{ reconciled: boolean; officeUid: string }> => {
		const url = new URL(apiV1Paths.admin.stripeReconcileOffice);
		try {
			return await baseAdmin.postRequest<{ reconciled: boolean; officeUid: string }>(url, {});
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}

export function createGetInvoiceByUidMethod(baseAdmin: IBaseAdmin, _baseUrl: string) {
	return async (invoiceId: string): Promise<IGetInvoiceByUidResponse> => {
		const url = new URL(buildStripeInvoicePath(invoiceId));
		try {
			return await baseAdmin.getRequest<IGetInvoiceByUidResponse>(url);
		} catch (err) {
			baseAdmin.onError(err);
			return Promise.reject(err);
		}
	};
}
